import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Upload, X, Plus, Trash2 } from "lucide-react";

/**
 * Website tab — public homepage CMS content (hero, services, about, accreditations).
 * Receives the full settings object and an update fn `u(field, value)`.
 */
export default function WebsiteTab({ settings, u }) {
  return (
    <div className="space-y-4">
      {/* Smart Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3">
        <Globe size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-blue-800">Customise your public booking website</p>
          <p className="text-xs text-blue-700 mt-0.5">All text below appears on your public homepage. Edit any field and click <strong>Save Changes</strong> to publish instantly. Tip — your salon name, phone, email and address all pull from the <em>Salon</em> tab above.</p>
        </div>
      </div>

      {/* Hero Section */}
      <Card className="p-6 rounded-2xl border shadow-sm space-y-4">
        <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>🌟 Hero Section</h3>
        <div>
          <Label className="text-xs">Hero Image (top-right on homepage)</Label>
          <div className="mt-2 flex items-start gap-4">
            <div className="w-32 h-24 rounded-xl overflow-hidden border bg-muted shrink-0">
              <img
                src={settings.hero_image_url || "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=300&h=200&fit=crop"}
                alt="Hero preview"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-2">
              <label className="cursor-pointer">
                <Button variant="outline" size="sm" className="rounded-xl pointer-events-none" asChild>
                  <span><Upload size={13} className="mr-1.5" /> Upload Image</span>
                </Button>
                <input
                  type="file" accept="image/*" className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => u('hero_image_url', ev.target.result);
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              {settings.hero_image_url && (
                <Button variant="ghost" size="sm" className="rounded-xl text-destructive text-xs h-7" onClick={() => u('hero_image_url', '')}>
                  <X size={11} className="mr-1" /> Remove (restore default)
                </Button>
              )}
              <p className="text-[10px] text-muted-foreground">JPG or PNG recommended. Appears in the right side of your hero banner.</p>
            </div>
          </div>
        </div>

        <Separator />
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Location Tag (pill under logo)</Label>
            <Input value={settings.hero_tag || ''} onChange={e => u('hero_tag', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Beauty salon in Staines, Surrey" />
          </div>
          <div>
            <Label className="text-xs">Headline — Main text</Label>
            <Input value={settings.hero_headline || ''} onChange={e => u('hero_headline', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Nail Salon" />
          </div>
          <div>
            <Label className="text-xs">Headline — Highlighted / coloured text</Label>
            <Input value={settings.hero_headline_location || ''} onChange={e => u('hero_headline_location', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. in Staines" />
            <p className="text-[10px] text-muted-foreground mt-1">This part appears in your brand colour</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Hero Description</Label>
            <Textarea value={settings.hero_description || ''} onChange={e => u('hero_description', e.target.value)} className="mt-1 rounded-xl" rows={3} placeholder="Your local beauty salon in ... specialising in..." />
          </div>
        </div>
        <Separator />
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={settings.new_client_promo_enabled !== false} onCheckedChange={v => u('new_client_promo_enabled', v)} />
            <Label className="text-xs font-medium">Show new client promotion banner</Label>
          </div>
          {settings.new_client_promo_enabled !== false && (
            <div className="grid md:grid-cols-3 gap-3 pl-1">
              <div>
                <Label className="text-xs">Discount Amount (£)</Label>
                <Input type="number" value={settings.new_client_promo_amount ?? 5} onChange={e => u('new_client_promo_amount', parseFloat(e.target.value) || 0)} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs">Promo Headline</Label>
                <Input value={settings.new_client_promo_text || ''} onChange={e => u('new_client_promo_text', e.target.value)} className="mt-1 rounded-xl" placeholder="£5 off for new clients!" />
              </div>
              <div>
                <Label className="text-xs">Promo Sub-text</Label>
                <Input value={settings.new_client_promo_sub || ''} onChange={e => u('new_client_promo_sub', e.target.value)} className="mt-1 rounded-xl" placeholder="Discount applied automatically at checkout" />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* How to Book */}
      <Card className="p-6 rounded-2xl border shadow-sm space-y-4">
        <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>📋 How to Book Section</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Section Title</Label>
            <Input value={settings.howtobook_title || ''} onChange={e => u('howtobook_title', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Book your nails in Staines online" />
          </div>
          <div>
            <Label className="text-xs">Section Subtitle</Label>
            <Input value={settings.howtobook_subtitle || ''} onChange={e => u('howtobook_subtitle', e.target.value)} className="mt-1 rounded-xl" placeholder="Whether you're looking for..." />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { key: 'step1', default_title: 'Choose your treatment', default_desc: 'Browse our services. Not sure? Our AI assistant can recommend the perfect treatment.' },
            { key: 'step2', default_title: 'Pick a time', default_desc: 'See real-time availability and choose a slot that works for you.' },
            { key: 'step3', default_title: "You're booked!", default_desc: 'Get instant confirmation with a calendar invite. Reschedule or cancel anytime.' },
          ].map((step, i) => (
            <div key={step.key} className="space-y-2 p-3 bg-muted/20 rounded-xl">
              <p className="text-[10px] font-semibold text-muted-foreground">STEP {i + 1}</p>
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={settings[`howtobook_${step.key}_title`] || ''} onChange={e => u(`howtobook_${step.key}_title`, e.target.value)} className="mt-1 rounded-xl text-xs" placeholder={step.default_title} />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea value={settings[`howtobook_${step.key}_desc`] || ''} onChange={e => u(`howtobook_${step.key}_desc`, e.target.value)} className="mt-1 rounded-xl text-xs" rows={3} placeholder={step.default_desc} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Services Section */}
      <Card className="p-6 rounded-2xl border shadow-sm space-y-4">
        <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>💅 Services Section</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Section Title</Label>
            <Input value={settings.services_section_title || ''} onChange={e => u('services_section_title', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Nail services in Staines" />
          </div>
          <div>
            <Label className="text-xs">Section Subtitle</Label>
            <Input value={settings.services_section_subtitle || ''} onChange={e => u('services_section_subtitle', e.target.value)} className="mt-1 rounded-xl" placeholder="From BIAB and builder gel to gel manicures..." />
          </div>
        </div>
      </Card>

      {/* About Section */}
      <Card className="p-6 rounded-2xl border shadow-sm space-y-4">
        <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>ℹ️ About Section</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Section Title</Label>
            <Input value={settings.about_title || ''} onChange={e => u('about_title', e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Your local beauty salon in Staines, Surrey" />
          </div>
          <div>
            <Label className="text-xs">Paragraph 1</Label>
            <Textarea value={settings.about_para1 || ''} onChange={e => u('about_para1', e.target.value)} className="mt-1 rounded-xl" rows={4} placeholder="Full description of your salon and services..." />
          </div>
          <div>
            <Label className="text-xs">Paragraph 2</Label>
            <Textarea value={settings.about_para2 || ''} onChange={e => u('about_para2', e.target.value)} className="mt-1 rounded-xl" rows={3} placeholder="Areas you serve, new client offer..." />
          </div>
        </div>
      </Card>

      {/* Accreditations */}
      <Card className="p-6 rounded-2xl border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>🏆 Accreditations & Certifications</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Upload logos and list your credentials — shown in the About section of your homepage</p>
          </div>
          <Button
            variant="outline" size="sm" className="rounded-xl"
            onClick={() => {
              const list = [...(settings.accreditations || [])];
              list.push({ id: Date.now().toString(), logo_url: '', heading: '', title: '', credentials: [''] });
              u('accreditations', list);
            }}
          >
            <Plus size={13} className="mr-1" /> Add Accreditation
          </Button>
        </div>

        {(!settings.accreditations || settings.accreditations.length === 0) ? (
          <div className="text-center py-8 text-sm text-muted-foreground border border-dashed border-border rounded-xl">
            No accreditations added yet. Click "Add Accreditation" to get started.
          </div>
        ) : (
          <div className="space-y-5">
            {(settings.accreditations || []).map((acc, idx) => (
              <div key={acc.id || idx} className="border border-border rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground">ACCREDITATION {idx + 1}</p>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive rounded-lg"
                    onClick={() => {
                      const list = [...(settings.accreditations || [])];
                      list.splice(idx, 1);
                      u('accreditations', list);
                    }}
                  ><Trash2 size={13} /></Button>
                </div>

                <div className="flex items-start gap-4">
                  <div className="shrink-0">
                    {acc.logo_url ? (
                      <div className="relative">
                        <img src={acc.logo_url} alt="Logo" className="h-14 w-14 object-contain rounded-xl border bg-white p-1" />
                        <button
                          onClick={() => {
                            const list = [...(settings.accreditations || [])];
                            list[idx] = { ...list[idx], logo_url: '' };
                            u('accreditations', list);
                          }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px]"
                        >✕</button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-16 h-16 border-2 border-dashed border-border rounded-xl cursor-pointer hover:bg-muted/20 transition-colors">
                        <Upload size={14} className="text-muted-foreground mb-1" />
                        <span className="text-[9px] text-muted-foreground">Logo</span>
                        <input type="file" accept="image/*" className="hidden" onChange={e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = ev => {
                            const list = [...(settings.accreditations || [])];
                            list[idx] = { ...list[idx], logo_url: ev.target.result };
                            u('accreditations', list);
                          };
                          reader.readAsDataURL(file);
                        }} />
                      </label>
                    )}
                  </div>

                  <div className="flex-1 grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Badge / Short Label</Label>
                      <Input value={acc.heading || ''} onChange={e => {
                        const list = [...(settings.accreditations || [])];
                        list[idx] = { ...list[idx], heading: e.target.value };
                        u('accreditations', list);
                      }} className="mt-1 rounded-xl text-xs" placeholder="e.g. ABT Accredited" />
                    </div>
                    <div>
                      <Label className="text-xs">Full Title</Label>
                      <Input value={acc.title || ''} onChange={e => {
                        const list = [...(settings.accreditations || [])];
                        list[idx] = { ...list[idx], title: e.target.value };
                        u('accreditations', list);
                      }} className="mt-1 rounded-xl text-xs" placeholder="e.g. ABT Accredited Nail Technician" />
                    </div>
                    <div className="md:col-span-2">
                      <div className="flex items-center justify-between mb-1.5">
                        <Label className="text-xs">Credential lines (one per line)</Label>
                        <button className="text-[10px] text-primary hover:underline" onClick={() => {
                          const list = [...(settings.accreditations || [])];
                          list[idx] = { ...list[idx], credentials: [...(list[idx].credentials || []), ''] };
                          u('accreditations', list);
                        }}>+ Add line</button>
                      </div>
                      <div className="space-y-1.5">
                        {(acc.credentials || ['']).map((cred, ci) => (
                          <div key={ci} className="flex gap-2">
                            <Input value={cred} onChange={e => {
                              const list = [...(settings.accreditations || [])];
                              const creds = [...(list[idx].credentials || [])];
                              creds[ci] = e.target.value;
                              list[idx] = { ...list[idx], credentials: creds };
                              u('accreditations', list);
                            }} className="rounded-xl text-xs h-8" placeholder={`Credential ${ci + 1}...`} />
                            {(acc.credentials || []).length > 1 && (
                              <button onClick={() => {
                                const list = [...(settings.accreditations || [])];
                                const creds = [...(list[idx].credentials || [])];
                                creds.splice(ci, 1);
                                list[idx] = { ...list[idx], credentials: creds };
                                u('accreditations', list);
                              }} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={13} /></button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
